import React, { FC, Fragment, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid, List as MuiList, Typography } from '@material-ui/core';

import { SearchDocuments } from 'api';
import { IClientSettingParams, IFilter } from 'types/app';
import { getSelectedFilterTitle, isAdminUser } from 'helpers';
import * as paths from 'paths';
import * as Links from 'Links';
import {
  DepartmentFilters,
  Desktop,
  LoaderComponents,
  LocationFilters,
  Mobile,
  MobileSubmenu,
  Queries,
  SidebarButton,
  SidebarItem,
  StickySidebar,
} from 'components';
import { useProtectedClient, useCurrentUser, useFollowed } from 'hooks';
import { getModeratedGroups } from '../../helpers';

const FilterSelect: FC = () => {
  const match = useRouteMatch<IClientSettingParams>();
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  const isRecommended = match.path === paths.groups;
  const isGroupsIOwn = match.path === paths.groupsIOwn;
  const isGroupsModerating = match.path === paths.groupsModerating;
  const isGroupsFollowing = match.path === paths.groupsFollowing;
  const isGroupsAll = match.path === paths.groupsAll;

  const client = useProtectedClient();
  const { allow_group_creation } = client;
  const currentUser = useCurrentUser();
  const { groups } = useFollowed();
  const managedGroups = getModeratedGroups(currentUser, groups);

  const filters: IFilter[] = [
    {
      component: Links.Groups,
      isSelected: isRecommended,
      textNode: t('common:recommended'),
    },
    {
      component: Links.GroupsFollowing,
      isSelected: isGroupsFollowing,
      textNode: t('common:following'),
    },
    {
      component: Links.GroupsModerating,
      isSelected: isGroupsModerating,
      textNode: t('common:managing'),
    },
    {
      component: Links.GroupsIOwn,
      isSelected: isGroupsIOwn,
      textNode: t('groups:groupsIOwn'),
    },
    {
      component: Links.GroupsAll,
      isSelected: isGroupsAll,
      textNode: t('admin:allGroups'),
    },
  ];

  if (!client.allow_group_creation) {
    filters.splice(2, managedGroups.length ? 0 : 1);
  }

  function toggleExpanded(isExpanded: boolean) {
    setExpanded(isExpanded);
  }

  function renderFilter(filter: IFilter, index: number) {
    const { component, isSelected, textNode } = filter;

    return (
      <SidebarItem
        color="inherit"
        key={index}
        link={component}
        isSelected={isSelected}
        textNode={textNode}
      />
    );
  }

  function renderCreateButton() {
    const isAdmin = isAdminUser(currentUser);

    return (
      (isAdmin || allow_group_creation) && (
        <SidebarButton component={Links.GroupCreate}>{t('components:newGroup')}</SidebarButton>
      )
    );
  }

  function renderLoader() {
    const { Content, List, Row } = LoaderComponents;

    return (
      <List rows={8}>
        {rowIndex => (
          <Row padding="8px 12px">
            <Grid item>
              <Content index={rowIndex} maxWidth={140} />
            </Grid>
          </Row>
        )}
      </List>
    );
  }

  function renderDesktop() {
    return (
      <StickySidebar>
        {renderCreateButton()}
        <MuiList component="nav">{filters.map(renderFilter)}</MuiList>
        <Queries.SearchAggregates document={SearchDocuments.Groups} loader={renderLoader()}>
          {({ departments, locations }) => (
            <Fragment>
              <DepartmentFilters departments={departments} context="groups" />
              <LocationFilters locations={locations} context="groups" />
            </Fragment>
          )}
        </Queries.SearchAggregates>
      </StickySidebar>
    );
  }

  function renderMobile() {
    return (
      <Queries.SearchAggregates document={SearchDocuments.Groups} loader={renderLoader()}>
        {searchAggregates => {
          const filterTitle = getSelectedFilterTitle(filters, searchAggregates, match.params);

          return (
            <MobileSubmenu
              expanded={expanded}
              onToggle={toggleExpanded}
              selected={<Typography variant="subtitle2">{filterTitle}</Typography>}
            >
              <MuiList component="nav">{filters.map(renderFilter)}</MuiList>
              <DepartmentFilters departments={searchAggregates.departments} context="groups" />
              <LocationFilters locations={searchAggregates.locations} context="groups" />
              {renderCreateButton()}
            </MobileSubmenu>
          );
        }}
      </Queries.SearchAggregates>
    );
  }

  return (
    <Fragment>
      <Desktop>{renderDesktop()}</Desktop>
      <Mobile>{renderMobile()}</Mobile>
    </Fragment>
  );
};

export default FilterSelect;
